import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useLocation } from 'react-router-dom';

import { NoSSR } from '@hbf/dsl';
import { WeWantToPayCTA, WeWantToPayModal } from 'ha/modules/WeWantToPay';
import { LoadableAlertbar } from 'ha/modules/Alertbar';
import { NavBar } from 'ha/modules/NavBar';
import { FooterContainer as Footer } from 'ha/modules/Footer';
import { NavBarSideContainer as NavBarSide } from 'ha/modules/NavBarSide';
import { FontInfo, LoadFonts } from 'ha/components/LoadFonts';
import { defaultFont } from 'ha/constants/fonts/default';
import { KrugerDialog } from 'ha/modules/Kruger';
import { LoadableAuthModal } from 'ha/modules/AuthModal';
import { LoadableVerifyEmailDialogRoot } from 'ha/modules/ActivationDialogs/VerifyEmailDialog';
import { useIntl } from 'ha/i18n';
import { GoogleOneTapLazy } from 'ha/modules/GoogleOneTap/GoogleOneTap.lazy';
import { StickyFooter } from './StickyFooter';
import { FixedFooterSection } from './FixedFooterSection';
import { Meta } from './Meta';
import { Favicons } from './Favicons';
import { ProgressBar } from './ProgressBar';

interface Props {
  activeFonts?: FontInfo[];
  fixedFooterContent?: React.ReactNode;
  globalBanner?: React.ReactNode;
  hasFooter?: boolean;
  hasHeader?: boolean;
  hasSidebar?: boolean;
  hasProgressBar?: boolean;
  hasStickyNav?: boolean;
  isAuthenticated: boolean;
  noFollowFooter?: boolean;
  pageBanner?: React.ReactNode;
  showBottomNav?: boolean;
  showMiddleMenu?: boolean;
  showSearchBar?: boolean;
  searchBarElement?: React.ReactNode;
  showWeWantToPay?: boolean;
  showMobileDrawerMenu?: boolean;
  showLogo?: boolean;
  advertiserLogo?: string;
  showActions?: boolean;
  flexContent?: boolean;
  showUkraineFlair?: boolean;
  showDashboardCTA?: boolean;
}

const useStyles = makeStyles()(() => ({
  contentContainer: {
    flexGrow: 1,
    width: 0,
  },
  mainContainer: {
    flexGrow: 1,
  },
  flex: {
    display: 'flex',
  },
}));

export const PageLayout: React.FC<Props> = ({
  activeFonts = [defaultFont],
  children,
  fixedFooterContent,
  globalBanner,
  hasFooter = true,
  hasHeader = true,
  hasSidebar = false,
  hasProgressBar = true,
  hasStickyNav = false,
  isAuthenticated,
  noFollowFooter = true,
  pageBanner,
  showBottomNav = true,
  showMiddleMenu = true,
  showSearchBar = false,
  searchBarElement,
  showWeWantToPay = false,
  showMobileDrawerMenu = true,
  showLogo = true,
  showActions,
  showDashboardCTA = false,
  advertiserLogo = '',
  flexContent,
  showUkraineFlair = false,
}) => {
  const { classes, cx } = useStyles();

  const { urlResolver } = useIntl();
  const location = useLocation();

  return (
    <StickyFooter>
      <Meta />
      <LoadFonts activeFonts={activeFonts} />
      <Favicons />
      <NoSSR>
        <LoadableAlertbar />
      </NoSSR>
      {globalBanner}
      {hasProgressBar && <ProgressBar />}
      {hasHeader && (
        <NavBar
          hasStickyNav={hasStickyNav}
          showMiddleMenu={showMiddleMenu}
          showSearchBar={showSearchBar}
          searchBarElement={searchBarElement}
          showLogo={showLogo}
          advertiserLogo={advertiserLogo}
          showActions={showActions}
          showMobileDrawerMenu={showMobileDrawerMenu}
          showUkraineFlair={showUkraineFlair}
          showDashboardCTA={showDashboardCTA}
        />
      )}
      <div>
        {pageBanner}
        {isAuthenticated && showWeWantToPay && (
          <NoSSR>
            <WeWantToPayCTA />
          </NoSSR>
        )}
      </div>
      <div className={cx(classes.mainContainer, classes.flex)}>
        {hasSidebar && <NavBarSide />}
        <div
          className={cx(classes.contentContainer, {
            [classes.flex]: flexContent,
          })}
        >
          {children}
        </div>
      </div>
      {hasFooter ? <Footer noFollow={noFollowFooter} /> : null}

      <NoSSR>
        <FixedFooterSection
          fixedFooterContent={fixedFooterContent}
          isAuthenticated={isAuthenticated}
          showBottomNav={showBottomNav}
        />
      </NoSSR>
      {isAuthenticated && showWeWantToPay && (
        <NoSSR>
          <WeWantToPayModal />
        </NoSSR>
      )}
      {!isAuthenticated && (
        <NoSSR>
          <LoadableAuthModal />
        </NoSSR>
      )}
      {urlResolver.isOnAccountPages(location.pathname) && (
        <NoSSR>
          <LoadableVerifyEmailDialogRoot />
        </NoSSR>
      )}
      <NoSSR>
        <KrugerDialog />
      </NoSSR>
      <GoogleOneTapLazy />
    </StickyFooter>
  );
};
